import { Box, Stack, TextField } from '@mui/material';
import MUIButton from '@mui/material/Button';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import moment from 'moment';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import type React from 'react';
import { Component, Fragment, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import CropMonitoringCard from '/@/components/crop-monitoring/cards';
import Dialog from '/@/components/dialog';
import EmailVerificationAlert from '/@/components/email-verification-alert';
import FieldEdit from '/@/components/field-edit';
import FieldviewLoginPopup from '/@/components/fieldview-login-popup';
import NewOverlayList from '/@/components/new-overlay-list';
import Popup from '/@/components/popup';
import Terms from '/@/components/terms';
import settings, { isExpertoBayer } from '/@/core/settings';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useFieldById, useMyFields, useMyFieldsInfinite } from '/@/hooks/fields';
import { useAllFormatedMaps, useHumidityMaps } from '/@/hooks/maps';
import { fetchDefaultCropConfig } from '/@/hooks/recommendation';
import { withRouter } from '/@/hooks/with-router';
import { _ } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { setField as setCropMonitoringField } from '/@/store/crop-monitoring/actions';
import { getCropMonitorings } from '/@/store/crop-monitoring/apiProviders';
import { deleteFieldFromAPI } from '/@/store/fields/apiProviders';
import { setGoBack, setSelectedFeature, setShowTermsAndConditions } from '/@/store/nav/actions';
import { getActiveView, getSelectedFeature, getShowTermsAndConditions } from '/@/store/nav/selectors';
import { getWeedsRecommendationEnabled } from '/@/store/private-data/recommendation-weeds/selectors';
import { createRecommendationFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { updateActiveRecommendation } from '/@/store/recommendations/actions';
import { setPreferences as setSessionPreferences } from '/@/store/session/actions';
import {
    getAppInstance,
    getCornRecommendationEnabled,
    getEnableSpodoptera,
    getMapsEnabled,
    getNitrogenRecommendationEnabled,
    getSessionCountry,
    getSoyRecommendationEnabled,
    getUser,
} from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { loadWeather, setField } from '/@/store/weather/actions';
import { getField, getWeatherFeatureEnabled } from '/@/store/weather/selectors';
import type { Field } from '/@/types/field';
import { parts } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import WeatherComponent from '/@/views/fields/field-detail/field-weather';
import { seedRecommendationUrl } from '/@/views/recommendations/seed-recommendation-route';
import CreateRecommendationModal from '../../dashboard/rv/components/create-recommendation-modal';
import { convertCountryBoundsToGoogleLatLng } from '../field-new-v2/utils';
import ButtonShowSidebar from './button-show-sidebar';
import FieldOptions from './components/actions';
import FieldsSidebar from './components/fields';
import FieldMap from './components/map';
import { MobileHeader } from './components/mobile-header';
import SidebarActions from './components/sidebar-actions';
import styles from './styles.module.css';

interface OverlayConfig {
    label: string;
    icon: string;
    color: string;
    enable: boolean;
    type?: string;
    id: string;
    notInteractive?: boolean;
    children?: React.ReactNode;
}

export const getOverlayData = () => {
    return {
        label: _('Enfermedades, Plagas y Malezas'),
        icon: 'weeds',
        color: 'blue',
        children: [
            {
                icon: 'corn',
                label: _('Maíz'),
                color: 'orange',
                type: 'corn',
                children: [
                    { label: _('Enfermedades'), type: 'corn_diseases_historic_pressure' },
                    { label: _('Plagas'), type: 'corn_pests_historic_pressure' },
                    { label: _('Malezas'), type: 'weeds_rem' },
                    { label: _('Caracteres Agrónomicos'), type: 'corn_others' },
                ],
                id: 'corn',
            },
            {
                icon: 'circleSpodoptera',
                label: _('Pronóstico de Spodoptera'),
                type: 'spodoptera_forecast',
                color: 'orange',
                id: 'spodoptera_forecast',
            },

            {
                icon: 'humidity',
                label: _('Humedad en el suelo'),
                type: 'humidity',
                color: 'blue',
                id: 'humidity',
            },
        ],
    } as const;
};

@connect(
    (state, props) => {
        const appInstance = getAppInstance(state);
        const fieldId = getField(state);

        return {
            offline: state.network.offline,
            sessionCountry: getSessionCountry(state),
            selectedFeature: getSelectedFeature(state),
            weatherFeatureEnabled: getWeatherFeatureEnabled(state),
            enableSpodoptera: getEnableSpodoptera(appInstance),
            field: fieldId,
            cornRecommendationEnabled: getCornRecommendationEnabled(state, appInstance),
            soyRecommendationEnabled: getSoyRecommendationEnabled(state, appInstance),
            weedsEnabled: getWeedsRecommendationEnabled(state),
            nitrogenRecommendationEnabled: getNitrogenRecommendationEnabled(state),
            mapsEnabled: getMapsEnabled(state),
            user: getUser(state),
            appInstance,
            activeView: getActiveView(state),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setSessionPreferences,
                setSelectedFeature,
                setField,
                loadWeather,
                setGoBack,
                trackEvent,
                setCropMonitoringField,
                updateActiveRecommendation,
            },
            dispatch
        )
)
@withRouter
@autobind
class FieldList extends Component {
    constructor(props) {
        super(props);
    }

    static propTypes = {
        tab: PropTypes.oneOf(['fields', 'overlays']),
        userTypeView: PropTypes.oneOf(['user', 'rv']),
        maps: PropTypes.any,
        humidityMaps: PropTypes.any,
        loading: PropTypes.bool,
        fieldsQuery: PropTypes.any,
        fieldName: PropTypes.any,
        setFieldName: PropTypes.func,
        fields: PropTypes.array,
        allFields: PropTypes.array,
        isMobile: PropTypes.bool,
        setOpenCreateRecommendationModal: PropTypes.func,
        setSelectedField: PropTypes.func,
        processedAllFields: PropTypes.array,
        processedFields: PropTypes.array,
    };

    static defaultProps = {
        tab: 'fields',
        userTypeView: 'user',
    };

    static contextTypes = {
        units: PropTypes.object,
        history: PropTypes.object,
        location: PropTypes.object.isRequired,
    };

    state = {
        query: '',
        activeView: 'map',
        mapFocused: false,
        showZoomedMap: false,
        field: {},
        showActions: false,
        edit: false,
        selectedField: '',
        overlayConfig: [],
        showWeeds: false,
        showDeleteConfirmation: false,
        showFields: true,
        showMap: true,
        enablePests: undefined,
        hidePanels: false,
        mapValues: [],
        fieldOptionsAnchorEl: null,
        showFrostRiskMap: false,
        showMaps: false,
        activeCropMonitoring: undefined,
        recommendationsModalOpened: false,
    };

    overlayData = getOverlayData();

    async componentDidMount() {
        let {
            selectedFeature: { selectedFeature },
        } = this.props;
        if (!selectedFeature) {
            selectedFeature = 'FieldDetail';
            this.props.setSelectedFeature({
                view: 'Home',
                selectedFeature: 'FieldDetail',
            });
        }

        if (!this.props.loading) {
            const filteredMaps = this.props.maps.filter((map) => {
                if (map.slug === 'spodoptera-forecast') {
                    return this.props.enableSpodoptera;
                }
                return true;
            });
            this.setState({ mapValues: filteredMaps });
        }

        if (selectedFeature === 'maps') {
            this.handleViewList();
        }
        this.filterConfig();

        // Only call focusMap if ref is set (not null) and no field is selected
        if (this.map && !this.state.selectedField) {
            setTimeout(() => {
                this.focusMap();
            }, 100);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        let field;
        if (
            this.props.isMobile &&
            (prevState.activeView !== this.state.activeView || prevState.showActions !== this.state.showActions)
        ) {
            if (this.state.activeView === 'list') {
                if (this.state.showActions) {
                    this.props.setGoBack(() => {
                        this.setState({
                            showActions: false,
                            showFields: true,
                            showWeeds: false,
                        });
                        this.handleViewList();
                    });
                } else {
                    this.props.setGoBack(() => this.handleViewMap());
                }
            }
            if (this.state.activeView == 'map') {
                this.props.setGoBack(undefined);
            }
        }

        if (this.props.maps !== prevProps.maps && !this.props.loading) {
            const filteredMaps = (this.props.maps || []).filter((map) => {
                if (map.slug === 'spodoptera-forecast') {
                    return this.props.enableSpodoptera;
                }
                return true;
            });
            this.setState({ mapValues: filteredMaps });
        }

        const searchParams = new URLSearchParams(this.props.location.search);
        const selectedField = searchParams.get('selectedField');
        if (this.props.sessionCountry.code !== prevProps.sessionCountry.code) {
            this.props.setField(null);
            this.setState({
                showActions: false,
                showWeeds: false,
                showFields: true,
                selectedOverlay: undefined,
                showFrostRiskMap: false,
                showMaps: false,
                selectedField: '',
            });
        }

        if (selectedField && !this.state.selectedField) {
            field = this.props.allFields.find((field) => field.id === Number(selectedField));
            if (field) {
                this.handleShowActions(field);
                setTimeout(() => {
                    this.fitMap(field);
                    this.map.setZoom(16);
                }, 2000);

                this.props.history({ search: searchParams.toString() }, { replace: true });
            }
        } else if (this.state.selectedField) {
            field = this.props.allFields.find((field) => field.id === this.state.selectedField.id);
        }
        if (prevState.activeCropMonitoring !== this.state.activeCropMonitoring) {
            this.handleSetOverlayConfig(field);
        }

        if (
            prevProps.sessionCountry.code !== this.props.sessionCountry.code ||
            (prevProps.allFields.length !== this.props.allFields.length && !field)
        ) {
            this.focusMap();
        }

        if (prevProps.selectedFeature !== this.props.selectedFeature) this.resetView();
        if (prevState.overlayConfig !== this.state.overlayConfig) {
            this.filterConfig();
        }
        if (prevState.enablePests !== this.state.enablePests) {
            this.handleSetOverlayConfig(field);
        }
        if (prevProps.location.hash === '#edit' && this.props.location.hash === '') {
            this.setState({
                showActions: false,
                showFields: true,
            });
            this.resetView();
        }
        if (prevProps.location.hash === '#pests' && this.props.location.hash === '#edit') {
            this.setState({
                showActions: true,
                showWeeds: false,
                showFields: false,
            });
        }
        if (field && field.name !== this.state.selectedField.name) {
            this.setState({
                selectedField: field,
                hidePanels: false,
                activeCropMonitoring: undefined,
            });
            this.handleShowActions(field);
        }

        if (
            prevProps.overlayType !== this.props.overlayType ||
            prevProps.overlayVersion !== this.props.overlayVersion
        ) {
            console.log('this.props.overlayType', this.props.overlayType);
            console.log('this.props.overlayVersion', this.props.overlayVersion);
            const overlay = this.state.mapValues.find(({ slug }) => slug === this.props.overlayType);
            console.log('overlay', overlay);
            if (!overlay) return;
            const version = this.props.overlayVersion
                ? overlay.versions.find(({ versionNumber }) => versionNumber === Number(this.props.overlayVersion))
                : overlay.versions[0];
            if (!version) return;
            this.setState({ selectedOverlay: { overlay, version } });
        }

        if (prevProps.activeView !== this.props.activeView) {
            if (this.props.activeView.name === 'NewHome') {
                this.props.setSelectedFeature({
                    view: 'NewHome',
                    selectedFeature: 'FieldDetail',
                });
            }
        }

        // Fix gray map
        if (
            (!this.state.showFrostRiskMap && this.state.showFrostRiskMap !== prevState.showFrostRiskMap) ||
            this.state.activeView !== prevState.activeView
        ) {
            setTimeout(() => {
                const center = this.getMapCenter();
                if (center) {
                    this.map?.map?.setCenter({
                        lng: center.lng + 0.000001,
                        lat: center.lat + 0.000001,
                    });
                    this.map?.map?.setZoom(center.zoom + 0.00001);
                }
            }, 1000);
        }
    }

    componentWillUnmount() {
        this.clearStates();
        this.props.setGoBack(undefined);
    }

    clearStates() {
        this.setState({ showFields: true });
    }

    resetView() {
        this.setState({ showZoomedMap: false });
        if (this.map) {
            this.map.setZoom(4);
        }
        this.setState({
            showActions: false,
            showWeeds: false,
            edit: false,
            selectedField: '',
        });
    }

    setMapRef(ref) {
        this.map = ref;
    }

    setDetailMapRef(ref) {
        this.map = ref;
    }

    focusMap() {
        if (!this.map) return;
        if (this.props.allFields.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            this.props.allFields.map(({ lat, lng }) => {
                bounds.extend(new google.maps.LatLng(lat, lng));
            });
            setTimeout(
                ({ map }) => {
                    if (!map) return;
                    map.fitBounds(bounds);
                    if (map.getZoom() > 16) map.setZoom(16);
                },
                100,
                { map: this.map }
            );
        } else if (this.props.sessionCountry) {
            const googleBounds = convertCountryBoundsToGoogleLatLng(this.props.sessionCountry.bounds);
            this.map.fitBounds(googleBounds);
        }
    }

    getMapCenter() {
        if (!this.map) return null;

        try {
            const center = this.map.getCenter?.();
            const zoom = this.map.getZoom?.();

            if (center && typeof center.lat === 'function' && typeof center.lng === 'function') {
                return {
                    lat: center.lat(),
                    lng: center.lng(),
                    zoom: zoom || 0,
                };
            }
        } catch (error) {
            console.warn('Error getting map center:', error);
        }

        return null;
    }

    filterFields(field) {
        const fieldPartials = parts(`${field.name} ${field.location}`);
        const queryPartials = parts(this.state.query);
        return (
            queryPartials.map((qp) => fieldPartials.find((fp) => fp.match(qp))).filter(Boolean).length ===
            queryPartials.length
        );
    }

    parseOverlay(selected) {
        if (!selected) return undefined;

        return [...this.state.mapValues].find(
            ({ type, slug }) => type === selected.overlay.type && slug === selected.overlay.slug
        )
            ? selected
            : undefined;
    }

    handleViewList() {
        this.setState({ activeView: 'list' });
    }

    fitMap(field) {
        if (field && this.map) {
            this.map.fitPoints(field.geometry, 16);
        }
    }

    handleViewMap() {
        const {
            selectedFeature: { selectedFeature },
        } = this.props;

        this.setState({ activeView: 'map' });

        if (selectedFeature === 'maps' && this.props.isMobile && this.state.mapFocused === false) {
            setTimeout(() => {
                this.focusMap();
            });
            this.setState({ mapFocused: true });
        }
    }

    handleQueryChange(query: string) {
        this.props.setFieldName(query);
    }

    handleQueryReset() {
        this.props.setFieldName('');
    }

    async handleMarkerClick({ id: fieldId, disabled }) {
        mpTrackEvent('Field detail opened', {
            entry_source: 'map',
        });

        // Cuando apreta desde el mapa
        const field = this.props.allFields.find((field) => field.id === fieldId);
        if (disabled) return;
        if (this.props.selectedFeature.selectedFeature === 'FieldDetail') {
            this.handleShowActions(field);
            this.handleViewList();
        } else {
            this.handleFieldClick(field);
        }
    }

    async handleFieldClick(field) {
        const { selectedFeature, crop } = this.props.selectedFeature;
        if (selectedFeature === 'weeds') {
            const cropConfig = await fetchDefaultCropConfig({
                field,
                crop: crop || 'corn',
            });

            const data = { selectedFeature };
            this.props.setSelectedFeature(data);

            const recommendation = await createRecommendationFromAPI({
                appInstance: this.props.appInstance.id,
                field: field.id,
                weeds: [
                    {
                        crop: cropConfig.crop,
                        defaultPlantingDate: cropConfig.defaultPlantingDate,
                        plantingDate: cropConfig.plantingDate,
                    },
                ],
            });

            return this.context.history(
                reverse('recommendations.RecommendationWeeds', {
                    params: { id: recommendation.id, field: field.id },
                })
            );
        }

        if (selectedFeature === 'h2h') {
            mpTrackEvent('H2H open', { entry_source: 'field-list' });
            return this.context.history(
                reverse('headToHead.HeadToHead', {
                    params: { fieldId: field.id, crop },
                })
            );
        }

        if (selectedFeature === 'recommendations') {
            mpTrackEvent('Productor home recommendations pop up opened', { entry_source: 'activity-section' });
            this.setState({ recommendationsModalOpened: true });
            return;
        }

        if (selectedFeature === 'recommendation') {
            return this.context.history(seedRecommendationUrl({ crop, fieldId: field.id, entrySource: 'field-list' }));
        }

        if (selectedFeature === 'carbonCalculator') {
            return this.context.history(reverse('CarbonCalculator', { params: { fieldId: field.id } }));
        }

        if (selectedFeature === 'carbonCalculatorBR') {
            return this.context.history(reverse('CarbonCalculatorBR', { params: { fieldId: field.id } }));
        }

        if (selectedFeature === 'maps') {
            return null;
        }

        if (selectedFeature === 'weather') {
            this.setState({ showZoomedMap: true });

            if (field !== this.state.field) {
                this.setState({ field });
            }
            this.fitMap(field);
            this.map.setZoom(16);
            this.props.setField(field.id);
            const payload = { lat: field.lat, lng: field.lng, id: field.id };
            await this.props.loadWeather(payload);
        }

        if (selectedFeature === 'weatherPhone') {
            this.props.setField(field.id);
            const payload = { lat: field.lat, lng: field.lng, id: field.id };
            await this.props.loadWeather(payload);
            return this.context.history(reverse('Weather', { params: { fieldId: field.id } }));
        }

        if (selectedFeature === 'productDatasheet') {
            return this.context.history(
                reverse('productDatasheet.ProductDatasheetBrandSelection', {
                    params: { field },
                })
            );
        }

        if (selectedFeature === 'FieldDetail') {
            this.context.history(reverse('fields.FieldDetail', { params: { fieldId: field.id } }));
        }
    }

    handleOverlayChange(overlay: { type: string; version: string }) {
        if (!overlay) return;
        let params = {};

        const parsed = queryString.parse(this.props.location.search);
        if (!overlay) {
            params = Object.entries(parsed).reduce((qs, [key, value]) => {
                if (!['overlayType', 'overlayVersion'].includes(key)) qs[key] = value;
                return qs;
            }, {});
        } else {
            const { type, version } = overlay;
            params = {
                ...parsed,
                overlayType: type,
                overlayVersion: version,
                tab: 'overlays',
            };
        }

        this.handleViewMap();
        return this.props.history({ search: queryString.stringify(params) });
    }

    handleOverlayDeeplinkClick({ overlay, version }) {
        return () => {
            this.handleOverlayChange({
                type: overlay.slug,
                version: version.versionNumber,
            });
        };
    }

    offLine(onlineValue) {
        if (this.props.offline) return 'offline';
        return onlineValue;
    }

    handleSetOverlayConfig(field) {
        const offLineColor = this.props.offline ? 'gray' : 'blue';

        const cropMonitoringItem = {
            label: _('Seguimiento de Cultivo'),
            icon: this.offLine('cropMonitoring'),
            color: this.props.offline ? 'gray' : 'orange',
            enable: this.props.appInstance.enableCropMonitoring,
            type: 'cropMonitoring',
            id: 'cropMonitoring',
            notInteractive: this.state.activeCropMonitoring,
            children: this.state.activeCropMonitoring ? (
                <CropMonitoringCard
                    isMobile={this.props.isMobile}
                    index={1}
                    hideFieldName
                    cropMonitoring={this.state.activeCropMonitoring}
                    trackingKey='cropMonitoring.detail.fromHome'
                    isHomeSidebar
                />
            ) : undefined,
        };
        const carbonCalculatorItem = ({ isBr }: { isBr?: boolean }): OverlayConfig => {
            return {
                label: _('Simulador de rotaciones sustentables'),
                icon: this.offLine('carbon'),
                color: offLineColor,
                enable: isBr
                    ? this.props.appInstance.enableCarbonCalculatorBr
                    : this.props.appInstance.enableCarbonCalculator,
                type: 'carbonCalculator',
                id: 'carbonCalculator',
            };
        };
        let overlayConfig: OverlayConfig[];
        if (this.props.userTypeView === 'rv') {
            overlayConfig = [
                {
                    label: _('Recomendaciones'),
                    icon: this.offLine('handCheck'),
                    color: this.props.offline ? 'gray' : 'orange',
                    enable:
                        field?.node.zones === undefined ||
                        (this.props.cornRecommendationEnabled &&
                            field?.node.zones.some((zone) => zone.type === 'corn')),
                    type: 'corn',
                    id: 'RVRecommendations',
                },
                cropMonitoringItem,

                carbonCalculatorItem({}),
                carbonCalculatorItem({ isBr: true }),
                {
                    label: _('Head to head Maíz'),
                    icon: 'corn',
                    color: 'orange',
                    enable:
                        this.props.appInstance.enableCornHeadToHead &&
                        checkInternal(this.props.user, this.props.appInstance) &&
                        (field?.node.zones === undefined || field?.node.zones.some((zone) => zone.type === 'corn')),
                    type: 'corn',
                    id: 'cornH2H',
                },
                {
                    label: _('Head to head Soja'),
                    icon: 'soy',
                    color: 'green',
                    enable:
                        this.props.appInstance.enableSoyHeadToHead &&
                        checkInternal(this.props.user, this.props.appInstance) &&
                        (field?.node.zones === undefined || field?.node.zones.some((zone) => zone.type === 'soy')),
                    type: 'soy',
                    id: 'soyH2H',
                },
            ];
        } else {
            overlayConfig = [
                {
                    label: _('Recomendación de Maíz'),
                    icon: this.offLine('corn'),
                    color: this.props.offline ? 'gray' : 'orange',
                    enable:
                        !isExpertoBayer() &&
                        (field?.node.zones === undefined ||
                            (this.props.cornRecommendationEnabled &&
                                field?.node.zones.some((zone) => zone.type === 'corn'))),
                    type: 'corn',
                    id: 'cornReco',
                },
                {
                    label: _('Recomendación de Soja'),
                    icon: this.offLine('soy'),
                    color: this.props.offline ? 'gray' : 'green',
                    enable:
                        !isExpertoBayer() &&
                        (field?.node.zones === undefined ||
                            (this.props.soyRecommendationEnabled &&
                                field?.node.zones.some((zone) => zone.type === 'soy'))),
                    type: 'soy',
                    id: 'soyReco',
                },
                cropMonitoringItem,
                {
                    label: _('Recomendación de Protección de Cultivos'),
                    icon: this.offLine('weeds'),
                    color: this.props.offline ? 'gray' : 'expertobayer',
                    enable: !isExpertoBayer() && this.props.appInstance.enableCropProtectionRecommendation,
                    type: 'cropProtection',
                    id: 'cropProtection',
                },
                {
                    label: _('Nitrógeno Smart'),
                    icon: this.offLine('nitrogen'),
                    color: this.props.offline ? 'gray' : 'orange',
                    enable:
                        !isExpertoBayer() &&
                        this.props.nitrogenRecommendationEnabled &&
                        field &&
                        field.geometry.length !== 1,
                    id: 'nsmart',
                },
                {
                    label: _('Recomendaciones'),
                    icon: this.offLine('handCheck'),
                    color: this.props.offline ? 'gray' : 'expertobayer',
                    enable:
                        isExpertoBayer() &&
                        (this.props.appInstance.enableCropProtectionRecommendation ||
                            field?.node.zones === undefined ||
                            (this.props.soyRecommendationEnabled &&
                                field?.node.zones.some((zone) => zone.type === 'soy')) ||
                            (this.props.cornRecommendationEnabled &&
                                field?.node.zones.some((zone) => zone.type === 'corn'))),
                    type: 'recommendations',
                    id: 'recommendations',
                },
                {
                    label: _('Enfermedades, Plagas y Malezas'),
                    icon: 'diseases',
                    color: 'blue',
                    enable: !isExpertoBayer() && this.state.enablePests,
                    id: 'pestsAndDeceases',
                },
                carbonCalculatorItem({}),
                carbonCalculatorItem({ isBr: true }),
                {
                    label: _('Manejo de malezas'),
                    icon: 'weeds',
                    color: 'brown',
                    enable: this.props.weedsEnabled,
                    id: 'weeds',
                },
                {
                    label: _('Head to head Maíz'),
                    icon: 'corn',
                    color: 'orange',
                    enable:
                        this.props.appInstance.enableCornHeadToHead &&
                        checkInternal(this.props.user, this.props.appInstance) &&
                        (field?.node.zones === undefined || field?.node.zones.some((zone) => zone.type === 'corn')),
                    type: 'corn',
                    id: 'cornH2H',
                },
                {
                    label: _('Head to head Soja'),
                    icon: 'soy',
                    color: 'green',
                    enable:
                        this.props.appInstance.enableSoyHeadToHead &&
                        checkInternal(this.props.user, this.props.appInstance) &&
                        (field?.node.zones === undefined || field?.node.zones.some((zone) => zone.type === 'soy')),
                    type: 'soy',
                    id: 'soyH2H',
                },
            ];
        }

        this.setState({
            overlayConfig,
        });
    }

    async filterPests(field) {
        if (!this.state.mapValues.length) return;
        if (!field.mapValues) return;
        const mapValues = field.mapValues.map((mv) => {
            const allValue = this.state.mapValues.find((element) => {
                return element.id === mv.map;
            });
            return { ...mv, map: allValue };
        });
        const children = this.overlayData.children
            .filter((item) => {
                if (item.children) return true;
                return mapValues.find((mv) => {
                    if (!mv.map) return false;
                    return mv.map.type === item.type;
                });
            })
            .map((item) => {
                if (!item.children) return item;
                return {
                    ...item,
                    children: item.children.filter((item) => {
                        if (item.children) return true;
                        return mapValues.find((mv) => {
                            if (!mv.map) return false;
                            return mv.map.type === item.type;
                        });
                    }),
                };
            })
            .filter((item) => {
                if (!item.children) return true;
                if (item.type === 'spodoptera_forecast') return this.props.enableSpodoptera;
                return item.children.length > 0;
            });

        this.setState({
            enablePests: Boolean(children.length),
        });
    }

    async handleShowActions(field) {
        const cropMonitorings = await getCropMonitorings({
            appInstanceId: this.props.appInstance.id,
            field: field.id,
        });
        const activeCropMonitorings = cropMonitorings.results.filter((cm) => cm.active);
        const hasCropMonitoring = activeCropMonitorings.length >= 1 && isExpertoBayer();
        this.setState({
            activeCropMonitoring: hasCropMonitoring ? activeCropMonitorings[0] : undefined,
        });
        try {
            this.filterPests(field);
        } catch (error) {}
        try {
            if (field.id != this.state.selectedField.id) {
                this.fitMap(field);
                this.map.setZoom(16);
            }
        } catch (error) {}
        if ((this.state.selectedOverlay || this.state.showFrostRiskMap) && !this.props.isMobile) {
            if (field.id == this.state.selectedField.id) {
                this.setState({
                    showFrostRiskMap: false,
                    selectedOverlay: undefined,
                });
            } else {
                this.setState({
                    selectedField: field,
                });
                return;
            }
        }
        this.setState({
            showZoomedMap: true,
            selectedField: field,
            showWeeds: false,
            showActions: true,
            edit: false,
            showDeleteConfirmation: false,
            showFields: !this.props.isMobile,
            hidePanels: false,
        });
        if (this.props.isMobile) this.context.history({ hash: '#edit' });
        this.handleSetOverlayConfig(field);
        this.props.setField(field.id);
        const payload = { lat: field.lat, lng: field.lng, id: field.id };
        if (!this.props.isMobile && this.props.weatherFeatureEnabled) await this.props.loadWeather(payload);
    }

    handleDelete() {
        deleteFieldFromAPI({
            fieldId: this.state.selectedField.id,
            country: this.props.sessionCountry.code,
        });
        this.setState({ showDeleteConfirmation: false });
        if (!this.props.offline) {
            this.map.setZoom(4);
        }
        if (this.props.isMobile) {
            this.setState({ showFields: true });
        }
        this.setState({
            showActions: false,
            showWeeds: false,
            edit: false,
            showZoomedMap: false,
            selectedField: '',
        });
        mpTrackEvent('Field deleted', undefined);
    }

    handleDeleteCancel() {
        this.setState({ showDeleteConfirmation: false });
    }

    handleConfirmDelete() {
        this.setState({ showDeleteConfirmation: true });
    }

    async handleEdit(field) {
        const fieldsInMobile = !this.props.isMobile;
        this.setState({
            edit: true,
            showActions: false,
            selectedField: field,
            showZoomedMap: true,
            showWeeds: false,
            showDeleteConfirmation: false,
            showFields: fieldsInMobile,
            hidePanels: false,
        });
        if (field.id != this.state.selectedField.id) {
            this.fitMap(field);
            this.map.setZoom(16);
        }
        this.props.setField(field.id);
        const payload = { lat: field.lat, lng: field.lng, id: field.id };
        if (!this.props.isMobile && this.props.weatherFeatureEnabled) await this.props.loadWeather(payload);
    }

    handleShowEdit() {
        this.setState({ fieldOptionsAnchorEl: null });
        this.setState({
            edit: true,
            showActions: false,
            showWeeds: false,
        });
    }

    handleCountryChangeAccept() {
        // Note: We set the first country assigned to the map.
        return this.props.setSessionPreferences({
            country: this.state.selectedOverlay.overlay.countryIds[0],
        });
    }

    handleCountryDialogReset() {
        const parsed = queryString.parse(this.props.location.search);
        const cleaned = Object.entries(parsed).reduce((qs, [key, value]) => {
            if (!['overlayType', 'overlayVersion'].includes(key)) qs[key] = value;
            return qs;
        }, {});
        return this.props.history({ search: queryString.stringify(cleaned) });
    }

    hideSideBar() {
        this.setState({ showActions: false, edit: false });
        this.resetView();
    }

    handleShowEditSidebar(field) {
        this.handleSetOverlayConfig(field);
        this.filterConfig();
        this.setState({
            edit: false,
            showActions: true,
            showWeeds: false,
        });
    }

    handleGoToNewField({ view, selectedFeature }) {
        const data = { view, selectedFeature };
        this.props.setSelectedFeature(data);
        this.context.history(reverse(view));
        const trackingKey = `${view}.${selectedFeature}`;
        this.props.trackEvent({ key: trackingKey.toLowerCase() });
    }

    renderOverlayDeeplink() {
        // TODO: Define if this is needed, it has been hidden for 2 years
        const { tab } = this.props;
        const type = 'spodoptera_forecast';
        const overlay = this.state.mapValues.find((overlay) => overlay.type === type);

        const selected = this.state.selectedOverlay;
        if (tab === 'overlays' || !overlay || (selected && selected.overlay.type === type)) return null;

        const version = overlay.versions[0];
        const created = moment(version.versionNumber.toString());

        if (created.isBefore(moment().subtract(2, 'weeks'))) return null;

        return (
            <div className={styles.overlayDeeplink} onClick={this.handleOverlayDeeplinkClick({ overlay, version })}>
                <div>
                    {_('Ver pronóstico de Spodoptera')}
                    <p>{_(version.versionLabel)}</p>
                </div>
                <i className='material-icons'>chevron_right</i>
            </div>
        );
    }

    goBackOverlay() {
        this.setState({ showActions: true, showWeeds: false });
    }

    filterConfig() {
        return this.state.overlayConfig.filter((item) => item.enable);
    }

    async selectItem(item, e) {
        e.stopPropagation();
        this.setState({ SelectedItem: item });
        const fieldId = this.state.selectedField.id;

        if (item.id === 'RVRecommendations') {
            mpTrackEvent('RV recommendations pop up opened', {
                entry_source: 'field-list',
            });
            this.props.setSelectedField(this.state.selectedField);
            this.props.setOpenCreateRecommendationModal(true);
            return;
        }

        if (item.label === _('Enfermedades, Plagas y Malezas')) {
            this.setState({ showWeeds: true });
            this.props.trackEvent({ key: 'home.deseasesPlaguesAndWeeds' });
            if (this.props.isMobile) this.context.history({ hash: '#pests' });
        }

        const crop = item.type;
        if (this.props.offline) return;
        if (item.label === _('Manejo de malezas')) {
            const cropConfig = await fetchDefaultCropConfig({
                field: this.state.selectedField,
                crop: crop || 'corn',
            });

            const recommendation = await createRecommendationFromAPI({
                appInstance: this.props.appInstance.id,
                field: fieldId,
                weeds: [
                    {
                        crop: cropConfig.crop,
                        defaultPlantingDate: cropConfig.defaultPlantingDate,
                        plantingDate: cropConfig.plantingDate,
                    },
                ],
            });

            this.props.trackEvent({ key: 'home.weeds.corn' });

            return this.context.history(
                reverse('recommendations.RecommendationWeeds', {
                    params: { id: recommendation.id, field: fieldId },
                })
            );
        }

        if (item.label === _('Recomendaciones')) {
            mpTrackEvent('Productor home recommendations pop up opened', {
                entry_source: 'field-list',
            });
            this.setState({ recommendationsModalOpened: true });
            return;
        }

        if (item.label === _('Recomendación de Maíz') || item.label === _('Recomendación de Soja')) {
            this.props.trackEvent({ key: `home.recommendation.${crop}` });
            return this.context.history(seedRecommendationUrl({ crop, fieldId, entrySource: 'field-list' }));
        }

        if (item.label === _('Head to head Maíz')) {
            this.props.trackEvent({ key: 'home.h2h.corn' });
            mpTrackEvent('H2H open', { entry_source: 'field-list' });
            return this.context.history(reverse('headToHead.HeadToHead', { params: { fieldId, crop } }));
        }
        if (item.label === _('Head to head Soja')) {
            this.props.trackEvent({ key: 'home.h2h.soy' });
            mpTrackEvent('H2H open', { entry_source: 'field-list' });
            return this.context.history(reverse('headToHead.HeadToHead', { params: { fieldId, crop } }));
        }
        if (item.label === _('Nitrógeno Smart')) {
            this.props.trackEvent({
                key: 'nitrogenrecommendation.nitrogenrecommendationlist.nitrogen',
            });
            mpTrackEvent('N-Smart Recommendation began', {
                entry_source: 'field-list',
            });
            return this.context.history(
                reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                    params: { fieldId },
                })
            );
        }

        if (item.type === 'carbonCalculator') {
            this.props.trackEvent({ key: 'home.carboncalculator' });
            return this.context.history(reverse('CarbonCalculator', { params: { fieldId } }));
        }

        if (item.type === 'carbonCalculatorBR') {
            this.props.trackEvent({ key: 'home.carboncalculator' });
            return this.context.history(reverse('CarbonCalculatorBR', { params: { fieldId } }));
        }

        if (item.type === 'cropMonitoring') {
            if (this.state.activeCropMonitoring) {
                return;
            }
            mpTrackEvent('Crop Monitoring opened', { entry_source: 'field-list' });
            this.props.trackEvent({
                key: 'cropmonitoring.cropmonitoring.cropmonitoring',
            });
            this.props.setCropMonitoringField(fieldId);

            if (isExpertoBayer()) {
                return this.context.history(
                    reverse('fields.FieldDetail', {
                        params: { fieldId: fieldId },
                    })
                );
            }
            return this.context.history(reverse('cropMonitoring.CropMonitoringCorn'));
        }
        if (item.type === 'cropProtection') {
            this.props.trackEvent({ key: 'home.cropProtection' });
            mpTrackEvent('Crop Protection Recommendation began', {
                entry_source: 'field-list',
            });
            return this.context.history(reverse('cropProtectionRecommendation', { qs: { fieldId } }));
        }
    }

    handleHumidityClick() {
        if (this.state.selectedField && !this.props.isMobile) {
            this.fitMap(this.state.selectedField);
            this.map.setZoom(16);
        }
        if (this.state.selectedOverlay) {
            this.setState({
                showFrostRiskMap: false,
                showMaps: false,
                showActions: false,
                edit: false,
                hidePanels: true,
                selectedOverlay: undefined,
            });
        } else {
            mpTrackEvent('Soil moisture map open', undefined);
            this.setState({
                showFrostRiskMap: false,
                showMaps: false,
                hidePanels: true,
                showActions: false,
                edit: false,
                selectedOverlay: {
                    overlay: this.props.humidityMaps[0],
                    version: this.props.humidityMaps[0].versions[0],
                },
            });
        }
    }

    handleShowFrostRisk() {
        if (!this.state.showFrostRiskMap) mpTrackEvent('Frost risk map open', undefined);
        this.setState((prevState) => ({
            showFrostRiskMap: !prevState.showFrostRiskMap,
            showActions: false,
            showMaps: false,
            edit: false,
            hidePanels: true,
            selectedOverlay: undefined,
        }));
    }

    handleShowMaps() {
        if (!this.state.showMaps) mpTrackEvent('Incidences map open', undefined);
        this.setState((prevState) => ({
            showMaps: !prevState.showMaps,
            showFrostRiskMap: false,
            showActions: false,
            edit: false,
            hidePanels: true,
            selectedOverlay: undefined,
        }));
    }

    render() {
        const { processedAllFields, processedFields, selectedFeature } = this.props;
        const enableSpodoptera = ['weather', 'FieldDetail'].every((el) => el !== selectedFeature.selectedFeature);

        const fields = isExpertoBayer()
            ? processedAllFields || processedFields || []
            : processedFields || processedAllFields || [];

        return (
            <Stack sx={{ height: '100%' }} spacing={2}>
                <CreateRecommendationModal
                    open={this.state.recommendationsModalOpened}
                    setOpen={() =>
                        this.setState({ recommendationsModalOpened: !this.state.recommendationsModalOpened })
                    }
                    selectedField={this.state.selectedField || undefined}
                    setSelectedField={(field) => {
                        if (field) {
                            this.setState({ selectedField: field });
                        }
                    }}
                    entrySource='field-list'
                />
                {this.state.showFields && this.props.userTypeView === 'rv' && (
                    <Stack
                        spacing={2}
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems='center'
                        justifyContent='space-between'
                    >
                        <TextField
                            fullWidth
                            label={_('Buscar')}
                            size='small'
                            value={this.props.fieldName}
                            onChange={(e) => this.handleQueryChange(e.target.value)}
                            onReset={this.handleQueryReset}
                            sx={{ width: { sm: '80%', md: '85%' } }}
                            disabled={this.props.fields.length === 0 && !this.props.fieldName}
                        />
                        <MUIButton
                            variant='contained'
                            sx={{ width: { xs: '100%', sm: '180px' } }}
                            color='secondary'
                            onClick={() =>
                                this.handleGoToNewField({
                                    view: 'fields.NewField',
                                    selectedFeature: 'FieldDetail',
                                })
                            }
                        >
                            {_('Agregar lote')}
                        </MUIButton>
                    </Stack>
                )}
                <div className={classNames(styles.root, styles[`show-${this.state.activeView}`])}>
                    <ButtonShowSidebar
                        expand={this.state.hidePanels}
                        onClick={() => {
                            if (this.state.hidePanels) {
                                this.setState({ activeView: 'list', showActions: true });
                            }
                            this.setState({ hidePanels: !this.state.hidePanels });
                        }}
                        showButton={
                            Boolean(this.state.selectedField) &&
                            !this.state.selectedOverlay &&
                            !this.state.showFrostRiskMap &&
                            !this.state.showMaps
                        }
                    >
                        {this.state.showFields && (
                            <Box
                                className={styles.sidebar}
                                sx={{
                                    height:
                                        this.props.userTypeView !== 'rv' || this.props.isMobile ? undefined : '600px',
                                }}
                            >
                                <FieldsSidebar
                                    userTypeView={this.props.userTypeView}
                                    handleGoToNewField={this.handleGoToNewField}
                                    showHeader={
                                        this.props.userTypeView === 'user' && this.props.selectedFeature !== 'maps'
                                    }
                                    selectedOverlay={this.state.selectedOverlay}
                                    mapValues={this.state.mapValues}
                                    handleOverlayChange={this.handleOverlayChange}
                                    fields={this.props.fields}
                                    handleViewList={this.handleViewList}
                                    handleViewMap={this.handleViewMap}
                                    fieldName={this.props.fieldName}
                                    handleQueryChange={this.handleQueryChange}
                                    handleQueryReset={this.handleQueryReset}
                                    mobileActiveView={this.state.activeView}
                                    filteredFields={this.props.processedFields.filter(this.filterFields)}
                                    fieldsQuery={this.props.fieldsQuery}
                                    selectedField={this.state.selectedField}
                                    hideSideBar={this.hideSideBar}
                                    handleFieldClick={this.handleFieldClick}
                                    handleShowActions={this.handleShowActions}
                                    handleEdit={this.handleEdit}
                                />
                            </Box>
                        )}
                        {this.state.showActions && !this.state.showWeeds && !this.state.hidePanels && (
                            <div className={styles.sidebar}>
                                <FieldOptions
                                    selectedField={this.state.selectedField}
                                    filteredOverlayConfig={this.filterConfig()}
                                    userTypeView={this.props.userTypeView}
                                    selectItem={(item, e) => this.selectItem(item, e)}
                                    edit={this.state.edit}
                                    setEdit={(edit) => this.setState({ edit, showActions: false })}
                                    handleConfirmDelete={this.handleConfirmDelete}
                                />
                            </div>
                        )}
                        {this.state.selectedField && this.state.showWeeds && (
                            <div className={styles.sidebar}>
                                <NewOverlayList field={this.state.selectedField} goBack={this.goBackOverlay} />
                            </div>
                        )}
                        {this.state.edit && !this.state.hidePanels && (
                            <Fragment>
                                <div className={styles.sidebar}>
                                    <FieldEdit
                                        field={this.state.selectedField}
                                        goBack={() => this.handleShowEditSidebar(this.state.selectedField)}
                                        deleteField={this.handleConfirmDelete}
                                    />
                                </div>
                            </Fragment>
                        )}
                        <Dialog
                            title={_('Eliminar lote')}
                            message={`${_('Estás seguro de que querés eliminar el lote')} "${
                                this.state.selectedField.name
                            }"?
                    ${_('Eliminando el lote se eliminarán todas las recomendaciones asociadas al mismo.')}
                    `}
                            visible={this.state.showDeleteConfirmation}
                            onClose={this.handleDeleteCancel}
                            onCancel={this.handleDeleteCancel}
                            onAccept={this.handleDelete}
                            alignCenter
                        />
                    </ButtonShowSidebar>
                    {this.props.activeView &&
                        ['NewHome', 'Home'].some((v) => {
                            return v === this.props.activeView.name;
                        }) &&
                        this.state.activeView == 'map' &&
                        isExpertoBayer() &&
                        this.props.isMobile && (
                            <MobileHeader
                                handleViewList={this.handleViewList}
                                handleViewMap={this.handleViewMap}
                                fieldName={this.props.fieldName}
                                handleQueryChange={this.handleQueryChange}
                                handleQueryReset={this.handleQueryReset}
                                userTypeView={this.props.userTypeView}
                                activeView={this.state.activeView}
                                fields={this.props.fields || []}
                            />
                        )}
                    <Dialog
                        visible={
                            Boolean(this.state.selectedOverlay && !this.parseOverlay(this.state.selectedOverlay)) &&
                            this.state.showMap
                        }
                        title={_('Cambiar de país')}
                        message={_('La capa seleccionada no pertenece al país activo. Querés cambiar el país?')}
                        acceptLabel={_('Cambiar país y mostrar capa')}
                        onClose={this.handleCountryDialogReset}
                        onCancel={this.handleCountryDialogReset}
                        onAccept={this.handleCountryChangeAccept}
                    />
                    {this.state.showMap && (
                        <div className={styles.content}>
                            {this.props.enableSpodoptera && enableSpodoptera && this.renderOverlayDeeplink()}
                            {this.props.weatherFeatureEnabled &&
                                !this.props.isMobile &&
                                this.state.showZoomedMap &&
                                !this.state.selectedOverlay &&
                                !this.state.showFrostRiskMap &&
                                !this.state.showMaps && <WeatherComponent fieldId={this.props.field} />}
                            <FieldMap
                                handleMarkerClick={this.handleMarkerClick}
                                showFrostRiskMap={this.state.showFrostRiskMap}
                                fields={fields}
                                selectedField={this.state.selectedField}
                                selectedOverlay={this.state.selectedOverlay}
                                handleOverlayChange={(overlay) => {
                                    if (overlay?.type && overlay?.version) {
                                        return this.handleOverlayChange(overlay);
                                    }
                                    if (overlay?.overlay?.slug && overlay?.version?.versionNumber) {
                                        return this.handleOverlayChange({
                                            type: overlay.overlay.slug,
                                            version: overlay.version.versionNumber,
                                        });
                                    }
                                }}
                                showMaps={this.state.showMaps}
                                parseOverlay={this.parseOverlay}
                                overlayData={this.overlayData}
                                maps={this.props.maps}
                                offline={this.props.offline}
                                setMapRef={this.setMapRef}
                                humidityMaps={this.props.humidityMaps}
                                handleHumidityClick={this.handleHumidityClick}
                                handleShowFrostRisk={this.handleShowFrostRisk}
                                handleShowMaps={this.handleShowMaps}
                                showTrialNetwork={this.props.showTrialNetwork === 'rv'}
                                mapsEnabled={this.props.mapsEnabled}
                            />
                        </div>
                    )}
                    {(this.state.showFields || this.state.showActions) && (
                        <SidebarActions
                            activeView={this.state.activeView}
                            userTypeView={this.props.userTypeView}
                            showFrostRiskMap={this.state.showFrostRiskMap}
                            showMaps={this.state.showMaps}
                            showActions={this.state.showActions}
                            showWeeds={this.state.showWeeds}
                            handleGoToNewField={this.handleGoToNewField}
                            fields={this.props.fields}
                            handleViewMap={this.handleViewMap}
                            handleViewList={this.handleViewList}
                        />
                    )}
                </div>
            </Stack>
        );
    }
}

const processFields = (fields, selectedFeature) => {
    let newFields = [...fields];

    let disabledErrorMessage = _('Lote no válido para la recomendación seleccionada.');
    if (selectedFeature.selectedFeature === 'nitrogen') {
        newFields = newFields.map((field) => {
            const disabled = field.geometry.length === 1;
            return {
                ...field,
                disabled,
                disabledErrorMessage,
            };
        });
    } else if (selectedFeature.selectedFeature === 'recommendation' || selectedFeature.selectedFeature === 'h2h') {
        if (selectedFeature.selectedFeature === 'h2h') {
            disabledErrorMessage = _('Lote no válido para la herramienta seleccionada.');
        }
        newFields = newFields.map((field) => {
            let disabled = false;
            if (field.node.zones !== undefined) {
                disabled = !field.node.zones.some((zone) => zone.type === selectedFeature.crop);
            }
            return {
                ...field,
                disabled,
                disabledErrorMessage,
            };
        });
    }

    return newFields;
};

const FieldListWrapper = ({
    tab,
    userTypeView = 'user',
    setOpenCreateRecommendationModal,
    setSelectedField,
    userNodeId,
    ...props
}: {
    tab: 'fields' | 'overlays';
    userTypeView?: 'user' | 'rv';
    setOpenCreateRecommendationModal: (value: boolean) => void;
    setSelectedField: (value: Field | undefined) => void;
    userNodeId?: number;
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const showTermsAndConditions = useSelector((state) => getShowTermsAndConditions(state));
    const actions = useMemo(() => bindActionCreators({ setShowTermsAndConditions }, dispatch), [dispatch]);
    const [fieldName, setFieldName] = useState('');
    const debouncedFieldName = useDebounce(fieldName, 1000);
    const country = useSelector(getSessionCountry);
    const { data: maps, isValidating: loading } = useAllFormatedMaps({
        country: country.code,
    });
    const { data: humidityMaps } = useHumidityMaps({ country: country.code });
    const fieldsQuery = useMyFieldsInfinite({
        name: debouncedFieldName,
        country: country.code,
        userNodeId,
    });
    const allFieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,name,location,altitude,area,geometry,externalProvider',
        userNodeId,
    });

    const fieldId = useMemo(() => {
        const query = new URLSearchParams(location.search);
        return query.get('selectedField');
    }, [location.search]);

    const fieldQuery = useFieldById({ id: Number(fieldId) });

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        query.delete('selectedField');
    }, [country, location.search]);

    const flatFields = useMemo(() => {
        let fields: Field[] = [];
        if (fieldQuery.data && fieldId) {
            fields = fields.concat(fieldQuery.data);
        }
        if (fieldsQuery.data) {
            fields = fields.concat(fieldsQuery.data?.flatMap((el) => el.results));
        }

        // Use a Set to track unique field IDs
        const uniqueFields = new Map();
        fields.forEach((field) => {
            if (!uniqueFields.has(field.id)) {
                uniqueFields.set(field.id, field);
            }
        });

        return [...uniqueFields.values()];
    }, [fieldQuery.data, fieldId, fieldsQuery.data]);
    const joinedAllFields = useMemo(() => {
        const allFields = (allFieldsQuery.data?.results || []).concat(flatFields);
        const mappedFieldsById = new Map(allFields.map((f) => [f.id, f]));
        return [...mappedFieldsById.values()].filter((f) => f?.name?.toLowerCase()?.includes(fieldName.toLowerCase()));
    }, [allFieldsQuery.data?.results, flatFields, fieldName]);

    const isMobile = useIsMobile();

    const selectedFeature = useSelector(getSelectedFeature);
    const processedAllFields = useMemo(
        () => processFields(joinedAllFields, selectedFeature),
        [joinedAllFields, selectedFeature]
    );
    const processedFields = useMemo(() => processFields(flatFields, selectedFeature), [flatFields, selectedFeature]);

    const provider = new URLSearchParams(location.search).get('provider');

    const [queryParams] = useSearchParams();

    return (
        <>
            {settings.app.theme === 'expertobayer' && <EmailVerificationAlert />}
            {/* <CropMonitoringModal /> */}
            <FieldList
                {...props}
                tab={tab}
                userTypeView={userTypeView}
                maps={maps}
                humidityMaps={humidityMaps}
                loading={loading}
                isMobile={isMobile}
                fieldsQuery={fieldsQuery}
                fieldName={fieldName}
                setFieldName={setFieldName}
                fields={flatFields}
                allFields={joinedAllFields}
                setOpenCreateRecommendationModal={setOpenCreateRecommendationModal}
                setSelectedField={setSelectedField}
                processedAllFields={processedAllFields}
                processedFields={processedFields}
                // Overlay Query Params
                overlayType={queryParams.get('overlayType')}
                overlayVersion={queryParams.get('overlayVersion')}
            />
            <Popup
                onClose={() => actions.setShowTermsAndConditions(false)}
                showCloseIcon
                visible={Boolean(showTermsAndConditions)}
                className={styles.popup}
            >
                <Terms />
            </Popup>

            {provider === 'fieldview' && (
                <FieldviewLoginPopup
                    handleClose={() => {
                        const url = new URL(window.location.href);
                        url.searchParams.delete('provider');
                        navigate(url.pathname + url.search + url.hash, { replace: true });
                    }}
                    message={_('¡Sincronizaste con Fieldview!')}
                />
            )}
        </>
    );
};

export default FieldListWrapper;
